import React from 'react';
import { Button } from '../../ui/Button';
import { ProgressBar } from '../ui/ProgressBar';
import { GridEditorActions } from '../../GridEditorPresenter';
import { ZoomIn, ZoomOut, Target, Merge, Pencil, Eye, Save, CheckCircle, XCircle, Search, Crosshair } from 'lucide-react';
import { PermissionTooltip, useCapability } from '../../../../rbac';

interface ToolbarProps {
  zoom: number;
  isSavingChanges: boolean;
  hasChanges: boolean;
  saveErrors: any[];
  lastSaveResult: any;
  isEditMode: boolean;
  searchQuery: string;
  saveProgress: {
    current: number;
    total: number;
    currentStep: string;
    percentage: number;
  };
  actions: Pick<GridEditorActions, 'setZoom' | 'resetView' | 'saveChanges' | 'clearSaveErrors' | 'fitMapToElements' | 'mergeElements' | 'toggleEditMode' | 'setSearchQuery'>;
  gridCanvasRef?: React.RefObject<{ setMapZoom: (zoom: number) => void; resetMapView: () => void }>;
  onFitToElements?: () => void;
}

export const Toolbar: React.FC<ToolbarProps> = ({
  zoom,
  isSavingChanges,
  hasChanges,
  saveErrors,
  lastSaveResult,
  isEditMode,
  searchQuery,
  saveProgress,
  actions,
  gridCanvasRef,
  onFitToElements
}) => {
  const canEditPlan = useCapability('planbt.edit');
  return (
    <div className="bg-white border-b border-slate-200">
      {/* Progress bar */}
      <ProgressBar
        current={saveProgress.current}
        total={saveProgress.total}
        percentage={saveProgress.percentage}
        currentStep={saveProgress.currentStep}
        isVisible={isSavingChanges && saveProgress.total > 0}
      />

      <div className="flex items-center justify-between px-3 py-2">
        {/* Left section: Mode toggle + tools */}
        <div className="flex items-center gap-1">
          {/* View/Edit mode toggle tabs */}
          <div className="flex bg-slate-100 rounded-lg p-0.5 mr-2">
            <button
              onClick={() => { if (isEditMode) actions.toggleEditMode(); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                !isEditMode
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Eye className="h-3.5 w-3.5" />
              Visualisation
            </button>
            <PermissionTooltip allowed={canEditPlan}>
              <button
                onClick={() => { if (!isEditMode) actions.toggleEditMode(); }}
                disabled={!canEditPlan}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isEditMode
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Pencil className="h-3.5 w-3.5" />
                Édition
              </button>
            </PermissionTooltip>
          </div>

          <div className="w-px h-6 bg-slate-200" />

          {/* Zoom controls */}
          <button
            onClick={() => {
              const newZoom = Math.min(zoom * 1.2, 3);
              actions.setZoom(newZoom);
              gridCanvasRef?.current?.setMapZoom(newZoom);
            }}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600 transition-colors"
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              const newZoom = Math.max(zoom / 1.2, 0.5);
              actions.setZoom(newZoom);
              gridCanvasRef?.current?.setMapZoom(newZoom);
            }}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600 transition-colors"
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>

          <button
            onClick={() => onFitToElements?.()}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600 transition-colors"
            title="Centrer sur les éléments"
          >
            <Crosshair className="h-4 w-4" />
          </button>

          <button
            onClick={() => {
              actions.resetView();
              gridCanvasRef?.current?.resetMapView();
            }}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600 transition-colors"
            title="Réinitialiser la vue"
          >
            <Target className="h-4 w-4" />
          </button>

          <button
            onClick={actions.mergeElements}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600 transition-colors"
            title="Fusionner les éléments empilés"
          >
            <Merge className="h-4 w-4" />
          </button>
        </div>

        {/* Right section: Search + Save */}
        <div className="flex items-center gap-2">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => actions.setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              className="w-48 pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent placeholder-slate-400"
            />
          </div>

          {/* Save button - only in edit mode */}
          {isEditMode && (
            <Button
              size="sm"
              onClick={actions.saveChanges}
              variant={
                saveErrors.length > 0 ? 'error' :
                lastSaveResult?.success ? 'success' :
                'primary'
              }
              loading={isSavingChanges}
              noFocus={true}
              disabled={!hasChanges && !lastSaveResult?.success && saveErrors.length === 0}
            >
              {!isSavingChanges && (
                <>
                  {saveErrors.length > 0 && <XCircle className="h-4 w-4 mr-1.5" />}
                  {lastSaveResult?.success && <CheckCircle className="h-4 w-4 mr-1.5" />}
                  {saveErrors.length === 0 && !lastSaveResult?.success && <Save className="h-4 w-4 mr-1.5" />}
                </>
              )}
              {isSavingChanges ? 'Sauvegarde...' :
               saveErrors.length > 0 ? `${saveErrors.length} erreur(s)` :
               lastSaveResult?.success ? 'Sauvegardé' :
               'Sauvegarder'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
